
import { useRef, useCallback } from "react";
import { Chat } from "../types";
import { fetchChatMessagesFromSupabase } from "../utils/supabaseDataUtils";
import { transformChatData } from "../utils/chatStateTransformers";

export function useChatCache() {
  const chatsCacheRef = useRef<{[chatId: string]: Chat}>({});
  const lastChatUpdateTimesRef = useRef<{[chatId: string]: number}>({});
  const fetchThrottleTimesRef = useRef<{[chatId: string]: number}>({});

  const getChatWithMessages = async (
    chat: Chat, 
    forceRefresh = false
  ) => {
    
    const chatId = chat.id;
    const now = Date.now();
    const lastUpdate = lastChatUpdateTimesRef.current[chatId] || 0;
    const throttleTime = fetchThrottleTimesRef.current[chatId] || 0;
    
    const cachedChat = chatsCacheRef.current[chatId];
    const timeSinceLastUpdate = now - lastUpdate;
    const isThrottled = now - throttleTime < 10000; // 10 seconds throttle
    
    try {
      // if (cachedChat && timeSinceLastUpdate < 120000 && isThrottled && !forceRefresh) {
      //   return cachedChat;
      // } 
      
      fetchThrottleTimesRef.current[chatId] = now;
      
      const messages = await fetchChatMessagesFromSupabase(chatId);
      // console.log(`Retrieved ${messages.length} messages for chat ${chatId}`);
      
      const transformedChat = transformChatData(chat, messages);
      
      chatsCacheRef.current[chatId] = transformedChat;
      lastChatUpdateTimesRef.current[chatId] = now;
      
      return transformedChat;
    } catch (error) {
      console.log(`Error fetching messages for chat ${chatId}:`, error);
      
      if (cachedChat) {
        console.log(`Using cached messages as fallback for chat ${chatId}`);
        return cachedChat;
      }
      
      return transformChatData(chat, []);
    }
  };

  const updateChatInCache = useCallback((chatId: string, updatedChat: Chat) => {
    chatsCacheRef.current[chatId] = updatedChat;
    lastChatUpdateTimesRef.current[chatId] = Date.now();
  }, []);

  const isChatCacheValid = useCallback((chatId: string, maxAge = 120000) => {
    const cachedChat = chatsCacheRef.current[chatId];
    if (!cachedChat) return false;
    
    const lastUpdate = lastChatUpdateTimesRef.current[chatId] || 0;
    const timeSinceLastUpdate = Date.now() - lastUpdate;
    
    return timeSinceLastUpdate < maxAge;
  }, []);

  return {
    getChatWithMessages,
    updateChatInCache,
    isChatCacheValid,
    chatsCacheRef
  };
}
